import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { LogOut, Sun, Moon, Loader2, Building2, X, Search, CalendarDays, ChevronDown } from 'lucide-react';
import { format, parseISO, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';

const LOGO_URL = '/logo.webp';

const SPACE_TYPE_LABELS = {
  meeting_room: 'Meeting Room',
  conference_room: 'Conference Room',
  day_pass: 'Day Pass',
  dedicated_desk: 'Dedicated Desk',
  event_space: 'Event Space',
  virtual_office: 'Virtual Office',
};

const DATE_PRESETS = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'custom', label: 'Custom Range' },
];

const STATUS_OPTIONS = [
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'completed', label: 'Completed' },
  { value: 'pending', label: 'Pending' },
  { value: 'pending_payment', label: 'Pending Payment' },
  { value: 'cancelled', label: 'Cancelled' },
];

// Popover + checkbox-list dropdown. An empty `selected` array means "no
// filter applied" (i.e. everything matches), not "match nothing".
const MultiSelectFilter = ({ label, options, selected, onChange, testId }) => {
  const [open, setOpen] = useState(false);

  const toggle = (value) => {
    onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value]);
  };

  const triggerLabel = selected.length === 0
    ? label
    : selected.length === 1
      ? (options.find(o => o.value === selected[0])?.label || selected[0])
      : `${selected.length} selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-full sm:w-[180px] justify-between font-normal"
          data-testid={testId}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronDown className="w-4 h-4 ml-2 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <div className="max-h-64 overflow-y-auto space-y-0.5">
          {options.map(opt => (
            <label
              key={opt.value}
              className="flex items-center gap-2 text-sm px-2 py-1.5 rounded hover:bg-accent cursor-pointer"
            >
              <Checkbox
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
                data-testid={`${testId}-option-${opt.value}`}
              />
              {opt.label}
            </label>
          ))}
        </div>
        {selected.length > 0 && (
          <div className="pt-2 mt-1 border-t">
            <Button variant="ghost" size="sm" className="w-full text-muted-foreground" onClick={() => onChange([])}>
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

// Mirrors the admin BookingsTab classification (frontend/src/components/admin/BookingsTab.js)
// so a booking's "type" reads the same way across dashboards.
const getBookingType = (booking) => {
  if (booking.notes?.startsWith('Walk-in:') || (booking.guest_name && !booking.is_internal_client && booking.created_by_role !== 'sales')) return 'Walk-in';
  if (booking.is_internal_client) return 'Booked by Client';
  if (booking.created_by_role === 'sales') return 'Sales Booking';
  if (booking.is_internal || booking.notes?.startsWith('Internal:')) return 'Internal';
  return 'Booking';
};

const formatMoney = (n) =>
  typeof n === 'number' && !Number.isNaN(n)
    ? `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
    : '—';

// Same field + same color mapping as the admin BookingsTab "Status" column
// (frontend/src/components/admin/BookingsTab.js: getStatusColor + booking.status)
// so the two dashboards never disagree on a booking's status.
const getStatusColor = (status) => {
  switch (status) {
    case 'confirmed': return 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30';
    case 'completed': return 'bg-primary/20 text-primary border-primary/30';
    case 'pending': return 'bg-amber-500/20 text-amber-500 border-amber-500/30';
    case 'pending_payment': return 'bg-yellow-500/20 text-yellow-600 border-yellow-500/30';
    case 'cancelled': return 'bg-red-500/20 text-red-500 border-red-500/30';
    default: return 'bg-secondary text-secondary-foreground';
  }
};

const LandlordDashboard = () => {
  const { user, api, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  const [bookings, setBookings] = useState([]);
  const [venues, setVenues] = useState([]);
  const [bookables, setBookables] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dateFilter, setDateFilter] = useState('all');
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [showStartCal, setShowStartCal] = useState(false);
  const [showEndCal, setShowEndCal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilters, setStatusFilters] = useState([]);
  const [spaceTypeFilters, setSpaceTypeFilters] = useState([]);

  const assignedVenueIds = user?.assigned_venue_ids || [];

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [bookingsRes, venuesRes, bookablesRes] = await Promise.all([
          api.get('/bookings'),
          api.get('/venues', { params: { include_inactive: true } }),
          api.get('/bookables', { params: { include_inactive: true } }),
        ]);
        if (cancelled) return;
        setBookings(bookingsRes.data || []);
        setVenues(venuesRes.data || []);
        setBookables(bookablesRes.data || []);
      } catch (err) {
        // Landlord dashboard is read-only — surface nothing on load failure
        // beyond an empty state; there is no action for the user to retry.
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [api]);

  const getBookable = (bookableId) => bookables.find(b => b.id === bookableId);
  const getVenueName = (venueId) => venues.find(v => v.id === venueId)?.name || '';
  const getDisplayName = (booking) =>
    booking.guest_name || booking.user_name || booking.organization_name || 'Registered User';

  const enrichedBookings = useMemo(() => {
    return bookings
      .map(b => {
        const bookable = getBookable(b.bookable_id);
        return {
          ...b,
          bookable_name: bookable?.name || '-',
          bookable_type: bookable?.bookable_type || null,
        };
      })
      .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings, bookables]);

  // Space types are limited to those actually offered in the landlord's
  // assigned building(s), not every type that exists system-wide.
  const availableSpaceTypes = useMemo(() => {
    const types = new Set(
      bookables
        .filter(b => assignedVenueIds.includes(b.venue_id))
        .map(b => b.bookable_type)
        .filter(Boolean)
    );
    return Array.from(types);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookables, assignedVenueIds.join(',')]);

  const getDateRange = () => {
    const now = new Date();
    switch (dateFilter) {
      case 'today': return { start: startOfDay(now), end: endOfDay(now) };
      case 'week': return { start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfWeek(now, { weekStartsOn: 1 }) };
      case 'month': return { start: startOfMonth(now), end: endOfMonth(now) };
      case 'custom': return { start: customStart ? startOfDay(customStart) : null, end: customEnd ? endOfDay(customEnd) : null };
      default: return null;
    }
  };

  const filteredBookings = useMemo(() => {
    let result = enrichedBookings;

    const range = getDateRange();
    if (range) {
      result = result.filter(b => {
        if (!b.booking_date) return false;
        const bd = parseISO(b.booking_date);
        if (range.start && bd < range.start) return false;
        if (range.end && bd > range.end) return false;
        return true;
      });
    }

    if (statusFilters.length > 0) {
      result = result.filter(b => statusFilters.includes(b.status));
    }

    if (spaceTypeFilters.length > 0) {
      result = result.filter(b => spaceTypeFilters.includes(b.bookable_type));
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(b => (
        b.id?.toLowerCase().includes(q) ||
        b.booking_date?.includes(q) ||
        getVenueName(b.venue_id)?.toLowerCase().includes(q) ||
        b.bookable_name?.toLowerCase().includes(q) ||
        getDisplayName(b)?.toLowerCase().includes(q)
      ));
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrichedBookings, dateFilter, customStart, customEnd, statusFilters, spaceTypeFilters, searchQuery]);

  const hasActiveFilters = dateFilter !== 'all' || statusFilters.length > 0 || spaceTypeFilters.length > 0 || !!searchQuery;
  const clearFilters = () => {
    setDateFilter('all');
    setCustomStart(null);
    setCustomEnd(null);
    setStatusFilters([]);
    setSpaceTypeFilters([]);
    setSearchQuery('');
  };

  const assignedVenueNames = assignedVenueIds
    .map(id => venues.find(v => v.id === id)?.name)
    .filter(Boolean);

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0a0b0d] text-foreground' : 'bg-[#faf6e7] text-foreground'}`}>
      <header className={`border-b ${isDark ? 'border-[#1e2128] bg-[#0a0b0d]' : 'border-[#e5dfd0] bg-white'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Clayworks" className="h-9 w-auto" />
            <div>
              <h1 className="font-heading text-lg font-bold">Landlord Dashboard</h1>
              <p className="text-sm text-muted-foreground">
                Welcome, {user?.name}
                {assignedVenueNames.length > 0 && ` · ${assignedVenueNames.join(', ')}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="landlord-theme-toggle">
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="landlord-logout-btn">
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        <div className={`rounded-xl border ${isDark ? 'border-[#1e2128] bg-[#0e1013]' : 'border-[#e5dfd0] bg-white'}`}>
          <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: isDark ? '#1e2128' : '#e5dfd0' }}>
            <h2 className="font-heading text-lg font-semibold">Bookings</h2>
            <span className="text-xs text-muted-foreground">{filteredBookings.length} of {enrichedBookings.length}</span>
          </div>

          {assignedVenueIds.length !== 0 && (
            <div className="px-4 py-3 border-b" style={{ borderColor: isDark ? '#1e2128' : '#e5dfd0' }}>
              {/* Date period presets */}
              <div className="flex flex-wrap gap-2 mb-3" data-testid="landlord-date-period-filter">
                {DATE_PRESETS.map(p => (
                  <Button
                    key={p.value}
                    size="sm"
                    variant={dateFilter === p.value ? 'default' : 'outline'}
                    onClick={() => { setDateFilter(p.value); if (p.value !== 'custom') { setCustomStart(null); setCustomEnd(null); } }}
                    data-testid={`landlord-date-filter-${p.value}`}
                  >
                    {p.value === 'custom' && <CalendarDays className="w-3.5 h-3.5 mr-1.5" />}
                    {p.label}
                  </Button>
                ))}
              </div>

              {/* Custom date range pickers */}
              {dateFilter === 'custom' && (
                <div className="flex flex-wrap items-center gap-3 mb-3 p-3 rounded-lg border border-dashed" data-testid="landlord-custom-date-range">
                  <Popover open={showStartCal} onOpenChange={setShowStartCal}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="min-w-[140px] justify-start" data-testid="landlord-custom-start-date">
                        <CalendarDays className="w-3.5 h-3.5 mr-2" />
                        {customStart ? format(customStart, 'MMM dd, yyyy') : 'Start Date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={customStart} onSelect={(d) => { setCustomStart(d); setShowStartCal(false); }} />
                    </PopoverContent>
                  </Popover>
                  <span className="text-muted-foreground text-sm">to</span>
                  <Popover open={showEndCal} onOpenChange={setShowEndCal}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="min-w-[140px] justify-start" data-testid="landlord-custom-end-date">
                        <CalendarDays className="w-3.5 h-3.5 mr-2" />
                        {customEnd ? format(customEnd, 'MMM dd, yyyy') : 'End Date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={customEnd} onSelect={(d) => { setCustomEnd(d); setShowEndCal(false); }} />
                    </PopoverContent>
                  </Popover>
                  {(customStart || customEnd) && (
                    <Button variant="ghost" size="sm" onClick={() => { setCustomStart(null); setCustomEnd(null); }} className="text-muted-foreground">
                      <X className="w-3.5 h-3.5 mr-1" /> Clear
                    </Button>
                  )}
                </div>
              )}

              {/* Search + Space Type + Status */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by ID, venue, space, guest, date..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 pr-8"
                    data-testid="landlord-search-input"
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <MultiSelectFilter
                  label="All Space Types"
                  options={availableSpaceTypes.map(type => ({ value: type, label: SPACE_TYPE_LABELS[type] || type }))}
                  selected={spaceTypeFilters}
                  onChange={setSpaceTypeFilters}
                  testId="landlord-filter-space-type"
                />
                <MultiSelectFilter
                  label="All Status"
                  options={STATUS_OPTIONS}
                  selected={statusFilters}
                  onChange={setStatusFilters}
                  testId="landlord-status-filter"
                />
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="landlord-filter-clear">
                    <X className="w-4 h-4 mr-1" /> Clear
                  </Button>
                )}
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : assignedVenueIds.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-6">
              <Building2 className="w-8 h-8 text-muted-foreground mb-3" />
              <p className="font-medium">No buildings assigned yet</p>
              <p className="text-sm text-muted-foreground mt-1">Contact your admin to get a building assigned to your account.</p>
            </div>
          ) : filteredBookings.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              {enrichedBookings.length === 0
                ? 'No bookings found for your assigned building(s).'
                : 'No bookings match the selected filters.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b" style={{ borderColor: isDark ? '#1e2128' : '#e5dfd0' }}>
                    <th className="text-left py-3 px-4 font-medium text-blue-600 dark:text-blue-400">Booking ID</th>
                    <th className="text-left py-3 px-4 font-medium text-blue-600 dark:text-blue-400">Date &amp; Time</th>
                    <th className="text-left py-3 px-4 font-medium text-blue-600 dark:text-blue-400">Space</th>
                    <th className="text-left py-3 px-4 font-medium text-blue-600 dark:text-blue-400">Guest</th>
                    <th className="text-left py-3 px-4 font-medium text-blue-600 dark:text-blue-400">Booking Type</th>
                    <th className="text-left py-3 px-4 font-medium text-blue-600 dark:text-blue-400">Amount</th>
                    <th className="text-left py-3 px-4 font-medium text-blue-600 dark:text-blue-400">Payment Method</th>
                    <th className="text-left py-3 px-4 font-medium text-blue-600 dark:text-blue-400">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-blue-600 dark:text-blue-400">Space Type</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((b) => (
                    <tr key={b.id} className="border-b last:border-b-0" style={{ borderColor: isDark ? '#1e2128' : '#e5dfd0' }}>
                      <td className="py-3 px-4 font-mono text-xs text-muted-foreground">#{b.id?.slice(0, 8)}</td>
                      <td className="py-3 px-4">
                        <div>{b.booking_date || '-'}</div>
                        <div className="text-xs text-muted-foreground">{b.start_time} - {b.end_time}</div>
                      </td>
                      <td className="py-3 px-4">{b.bookable_name}</td>
                      <td className="py-3 px-4 font-medium">{getDisplayName(b)}</td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className="font-normal text-muted-foreground">{getBookingType(b)}</Badge>
                      </td>
                      <td className="py-3 px-4 font-medium">{formatMoney(b.total_amount)}</td>
                      <td className="py-3 px-4">
                        <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30 border">Razorpay</Badge>
                      </td>
                      <td className="py-3 px-4">
                        <Badge className={`${getStatusColor(b.status)} border`}>{b.status?.replace('_', ' ')}</Badge>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="outline">{SPACE_TYPE_LABELS[b.bookable_type] || b.bookable_type || '-'}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default LandlordDashboard;
